import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import salesPipelineService from '../../../services/salesPipelineService';
import { toast } from 'react-toastify';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

const PipelineReports = () => {
  const navigate = useNavigate();
  const [byStage, setByStage] = useState([]);
  const [winLoss, setWinLoss] = useState({ won: 0, lost: 0, open: 0 });
  const [ownerPerf, setOwnerPerf] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [stageRes, wlRes, ownerRes] = await Promise.all([
        salesPipelineService.getPipelineByStage(),
        salesPipelineService.getWinLossCount(),
        salesPipelineService.getOwnerPerformance()
      ]);
      if (stageRes?.success) setByStage(stageRes.data || []);
      if (wlRes?.success) setWinLoss(wlRes.data || { won: 0, lost: 0, open: 0 });
      if (ownerRes?.success) setOwnerPerf(ownerRes.data || []);
    } catch (e) {
      toast.error('Failed to load pipeline reports');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Sales', path: '/sales', onClick: () => navigate('/sales') },
    { label: 'Reports', path: '/sales?tab=reports', onClick: () => navigate('/sales?tab=reports') },
    { label: 'Pipeline Report', path: '/sales/reports/pipeline', isCurrent: true }
  ];

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} /></div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading pipeline reports...</p>
        </div>
      </div>
    );
  }

  const chartData = byStage.map(s => ({
    name: s.stageName,
    total: s.totalValue,
    weighted: s.weightedValue,
    count: s.count
  }));

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} /></div>
      <div className="page-header">
        <h1>Pipeline Reports</h1>
        <p>Pipeline by stage, win/loss analysis, owner performance</p>
      </div>

      <div className="section-header" style={{ marginBottom: '12px' }}>
        <h3>Win/Loss Count</h3>
      </div>
      <div className="dashboard-metrics-grid mb-4">
        <div className="metric-card customers">
          <div className="metric-value">{winLoss.won}</div>
          <div className="metric-title">Won</div>
          <div className="metric-subtitle"><span className="badge bg-success">Won</span></div>
        </div>
        <div className="metric-card churn">
          <div className="metric-value">{winLoss.lost}</div>
          <div className="metric-title">Lost</div>
          <div className="metric-subtitle"><span className="badge bg-danger">Lost</span></div>
        </div>
        <div className="metric-card revenue">
          <div className="metric-value">{winLoss.open}</div>
          <div className="metric-title">Open</div>
          <div className="metric-subtitle"><span className="badge bg-info">Open</span></div>
        </div>
      </div>

      <div className="section-header" style={{ marginBottom: '12px' }}>
        <h3>Pipeline by Stage</h3>
      </div>
      <div className="dashboard-chart-card revenue-chart mb-4">
        <div className="chart-content" style={{ padding: '1rem' }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `₹${v / 1000}k`} />
                <Tooltip formatter={(v) => [formatCurrency(v), 'Amount']} labelFormatter={(l) => `Stage: ${l}`} />
                <Legend />
                <Bar dataKey="total" name="Total Value" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="weighted" name="Weighted Value" fill="var(--info)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No pipeline data</div>
          )}
        </div>
      </div>

      <div className="section-header" style={{ marginBottom: '12px' }}>
        <h3>Owner Performance</h3>
      </div>
      <div className="card">
        <div className="card-body">
          {ownerPerf.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th>Total Deals</th>
                    <th>Open</th>
                    <th>Won</th>
                    <th>Lost</th>
                    <th>Won Value</th>
                    <th>Win Rate %</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerPerf.map((o) => (
                    <tr key={o.ownerUserId}>
                      <td>{o.ownerName || 'Unassigned'}</td>
                      <td>{o.totalDeals}</td>
                      <td>{o.open}</td>
                      <td><span className="badge bg-success">{o.won}</span></td>
                      <td><span className="badge bg-danger">{o.lost}</span></td>
                      <td>{formatCurrency(o.wonValue)}</td>
                      <td>{o.winRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0">No owner performance data</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineReports;
